import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { fmt } from '@nyce/admin-kit';
import { Button, Chip, Select, TextField } from '@nyce/admin-kit/ui';
import {
  EmptyState,
  ErrorBox,
  Kpi,
  Loading,
  Page,
  TableScroller,
  useAsync,
} from '../../../components/common';
import { useTargets } from '../../../identity/session';
import { qualityFor, type RequestAuditItem } from '../../api/quality';
import { duration, money } from '../../shared';
import { useUrlParams, when } from './params';
import '../../buddy.css';
import './quality.css';

const KINDS = ['chat', 'replay', 'mcp', 'admin', 'login', 'attachment', 'pdf', 'api', 'monitor'];
const PERIODS = [
  { value: '1h', label: 'Last hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
];
const LIMITS = ['100', '200', '500'];

const cost4 = (v?: number | null) => (v == null ? '' : `$${v.toFixed(4)}`);

/** The chat's link to a conversation in that environment's nyce.buddy. */
const conversationUrl = (buddyUrl: string, id: string) =>
  `${buddyUrl.replace(/\/+$/, '')}/?conversation=${encodeURIComponent(id)}`;

/** Every request nyce.buddy served, summarised per kind, with tokens and cost where a model ran. Filters in the URL. */
export function BuddyRequestAuditPage() {
  const { active, targets } = useTargets();
  const buddyUrl = targets.find((t) => t.id === active)?.buddyUrl ?? null;
  const { get, pick, set } = useUrlParams();
  const period = pick(
    'period',
    PERIODS.map((p) => p.value),
    '7d'
  );
  const tenantId = get('tenant');
  const systemId = get('system');
  const kind = get('kind');
  const user = get('user');
  const limit = pick('limit', LIMITS, '200');
  const [userDraft, setUserDraft] = useState(user);
  useEffect(() => setUserDraft(user), [user]);
  const api = qualityFor(active);
  const tenants = useAsync(() => api.tenants(), [active]);
  const systems = useAsync(() => api.systems(), [active]);
  const data = useAsync(
    () =>
      api.requests({
        period,
        tenantId: tenantId || undefined,
        systemId: systemId || undefined,
        kind: kind || undefined,
        user: user || undefined,
        limit: Number(limit),
      }),
    [active, period, tenantId, systemId, kind, user, limit]
  );
  const tenantName = (id?: string | null) => (id ? (tenants.data?.find((t) => t.id === id)?.name ?? id) : '');
  const systemName = (id?: string | null) => (id ? (systems.data?.find((s) => s.id === id)?.name ?? id) : '');
  const visibleSystems = (systems.data ?? []).filter((s) => !tenantId || s.tenantId === tenantId);
  const commitUser = () => userDraft.trim() !== user && set({ user: userDraft.trim() });
  const d = data.data;
  const failed = (r: RequestAuditItem) => r.status >= 400;
  return (
    <Page
      title="Request audit"
      description="Every request nyce.buddy served, by kind, with tokens and cost where a model ran."
      actions={
        <div className="bd-toolbar">
          <Select
            aria-label="Period"
            value={period}
            onChange={(v) => set({ period: v === '7d' ? null : v })}
            options={PERIODS}
          />
          <Select
            aria-label="Tenant"
            value={tenantId}
            onChange={(v) => set({ tenant: v, system: null })}
            options={[
              { value: '', label: 'All tenants' },
              ...(tenants.data ?? []).map((t) => ({ value: t.id, label: t.name || t.id })),
            ]}
          />
          <Select
            aria-label="System"
            value={systemId}
            onChange={(v) => set({ system: v })}
            options={[
              { value: '', label: 'All systems' },
              ...visibleSystems.map((s) => ({ value: s.id, label: s.name || s.id })),
            ]}
          />
          <Select
            aria-label="Kind"
            value={kind}
            onChange={(v) => set({ kind: v })}
            options={[{ value: '', label: 'All kinds' }, ...KINDS.map((k) => ({ value: k, label: k }))]}
          />
          <TextField
            aria-label="User"
            className="nyk-search"
            type="search"
            placeholder="User code"
            value={userDraft}
            onChange={(e) => setUserDraft(e.target.value)}
            onBlur={commitUser}
            onKeyDown={(e) => e.key === 'Enter' && commitUser()}
          />
          <Select
            aria-label="Limit"
            value={limit}
            onChange={(v) => set({ limit: v === '200' ? null : v })}
            options={LIMITS.map((v) => ({ value: v, label: `${v} rows` }))}
          />
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={data.loading}
            onClick={() => void data.reload()}
          >
            Refresh
          </Button>
        </div>
      }
    >
      <ErrorBox error={data.error} />
      <Loading loading={data.loading && !d} />
      {d && (
        <div className={data.loading ? 'oph-dash-stale' : undefined} aria-busy={data.loading}>
          {d.summary.length ? (
            <div className="nyk-kpis" data-testid="audit-summary">
              {d.summary.map((s) => (
                <Kpi
                  key={s.kind}
                  label={s.kind}
                  value={fmt(s.requests)}
                  sub={`${fmt(s.tokensIn)} in · ${fmt(s.tokensOut)} out · ${money(s.cost)} · ${duration(s.averageMs)} avg`}
                />
              ))}
            </div>
          ) : (
            <EmptyState card title="No requests in this period." />
          )}
          <div className="nyk-card">
            <div className="nyk-card-head">
              <h2>Requests</h2>
              <span className="nyk-ui-hint">
                {d.items.length} shown{d.since ? ` since ${when(d.since)}` : ''}
              </span>
            </div>
            <TableScroller>
              <table className="nyk-ui-table nyk-cards" data-testid="audit-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Kind</th>
                    <th className="nyk-ui-num">Status</th>
                    <th>Tenant</th>
                    <th>System</th>
                    <th>User</th>
                    <th className="nyk-ui-num">Duration</th>
                    <th>Model</th>
                    <th className="nyk-ui-num">Tokens in / out</th>
                    <th className="nyk-ui-num">Cost</th>
                    <th>Question</th>
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((r) => (
                    <tr key={r.id} data-audit={r.id}>
                      <td className="nyk-card-title" title={`${r.method} ${r.path}`}>
                        {when(r.at)}
                        <div className="nyk-ui-hint nyk-mono">
                          {r.method} {r.path}
                        </div>
                      </td>
                      <td data-label="Kind">
                        <Chip size="sm">{r.kind}</Chip>
                      </td>
                      <td data-label="Status" className="nyk-ui-num">
                        <span className={failed(r) ? 'nyk-text-red' : undefined}>{r.status}</span>
                      </td>
                      <td data-label="Tenant">{tenantName(r.tenantId)}</td>
                      <td data-label="System">{systemName(r.systemId)}</td>
                      <td data-label="User">{r.user ?? ''}</td>
                      <td data-label="Duration" className="nyk-ui-num">
                        {duration(r.durationMs)}
                      </td>
                      <td data-label="Model">{r.model ?? ''}</td>
                      <td data-label="Tokens in / out" className="nyk-ui-num">
                        {r.tokensIn != null || r.tokensOut != null
                          ? `${fmt(r.tokensIn ?? 0)} / ${fmt(r.tokensOut ?? 0)}`
                          : ''}
                      </td>
                      <td data-label="Cost" className="nyk-ui-num">
                        {cost4(r.cost)}
                      </td>
                      <td data-label="Question">
                        <span className="bd-clip" title={r.question ?? ''}>
                          {r.question ?? ''}
                        </span>
                        {r.conversationId && buddyUrl && (
                          <a
                            href={conversationUrl(buddyUrl, r.conversationId)}
                            target="_blank"
                            rel="noreferrer"
                            className="nyk-link"
                          >
                            Conversation
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroller>
            {!d.items.length && <EmptyState title="No requests match these filters." />}
          </div>
        </div>
      )}
    </Page>
  );
}
